import {
  AccountProvider,
  BookingQueryParams,
} from '@collinsonx/constants/enums';
import { decodeJWT, verifyJWT } from '@collinsonx/jwt';
import { LinkedAccount, Maybe, Membership } from '@collinsonx/utils';
import {
  ApolloError,
  useLazyQuery,
  useMutation,
} from '@collinsonx/utils/apollo';
import updateConsumer from '@collinsonx/utils/mutations/updateConsumer';
import { getConsumerByID } from '@collinsonx/utils/queries';
import { getItem, setItem } from '@lib';
import { sendErrorLog, sendInfoLog, sendWarnLog } from '@lib/logging';
import { createInstance, ReactSDKClient } from '@optimizely/react-sdk';
import { BookingError, BookingInfo, BookingWarn } from 'config/datadog';
import {
  JWT,
  LANGUAGE,
  LOUNGE_CODE,
  PLATFORM,
  REFERRER,
  VERSION,
} from 'config/storage';
import useLocale from 'hooks/useLocale';
import { accountIsEqual, consumerIsValid, hasRequired } from 'lib/index';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  SessionContextType,
  signOut,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';
import { BridgePayload } from 'types/booking';
import { Consumer } from 'types/consumer';
import { formatLanguageParam } from 'utils/formatLanguageParam';

const { LK } = AccountProvider;

const {
  jwt: jwtParam,
  ln,
  loungeCode: lcParam,
  platform: platformParam,
  referrer: referrerParam,
  version,
} = BookingQueryParams;

export type PayloadState = {
  accountProvider?: string;
  consumerData: Consumer | undefined;
  consumerID?: string;
  fetchConsumerError?: ApolloError;
  fetchConsumerLoading: boolean;
  jwt: string | undefined;
  layoutError: string | undefined;
  layoutErrorParams: any | undefined;
  linkedAccount?: LinkedAccount;
  linkedAccountId: string | undefined;
  locale: string;
  loungeCode: string | undefined;
  membership?: Maybe<Membership>;
  membershipType?: string;
  optimizelyIsReady: boolean;
  payload: BridgePayload | undefined;
  payloadError: boolean | string;
  platform: string | undefined;
  referrerUrl: string | undefined;
  session: SessionContextType;
  setConsumerData: (consumer: Consumer) => void;
  setLayoutError: (err: string) => void;
  setLayoutErrorParams: (params: any | undefined) => void;
  setMatchedAccount: (matchedAccount: LinkedAccount) => void;
  setPayload: (payload: BridgePayload) => void;
  setPayloadError: (err: boolean | string) => void;
  sourceCode?: string;
  tracking?: ReactSDKClient;
  userIsSignedIn?: boolean;
};

export const PayloadContext = createContext<null | PayloadState>(null);

const optimizely = createInstance({
  sdkKey: process.env.NEXT_PUBLIC_SDK_OPTIMEZELY,
});

export const isPayloadValid = (payload?: BridgePayload) =>
  hasRequired(payload, ['accountProvider', 'externalId']);

export const PayloadProvider = (props: PropsWithChildren) => {
  const router = useRouter();
  const session = useSessionContext();

  const [payload, setPayload] = useState<BridgePayload>();
  const [loungeCode, setLoungeCode] = useState<string>();
  const [jwt, setJWT] = useState<string>();
  const [payloadError, setPayloadError] = useState<boolean | string>(false);
  const [matchedAccount, setMatchedAccount] = useState<LinkedAccount>();
  const [referrerUrl, setReferrerUrl] = useState<string>();
  const [platform, setPlatform] = useState<string>();
  const [layoutError, setLayoutError] = useState<string>();
  const [layoutErrorParams, setLayoutErrorParams] = useState<any | undefined>();
  const [consumerData, setConsumerData] = useState<Consumer>();
  const [language, setLanguage] = useState<string>('en');
  const [optimizelyIsReady, setOptimizelyIsReady] = useState<boolean>(false);

  const [
    fetchConsumer,
    { error: fetchConsumerError, loading: fetchConsumerLoading },
  ] = useLazyQuery(getConsumerByID);

  const [updateConsumerCall] = useMutation(updateConsumer);

  const translations = useLocale();

  const secret = process.env.NEXT_PUBLIC_JWT_SECRET || '';

  const logData = {
    consumerData,
    jwt,
    linkedAccountId: matchedAccount?.id,
    loungeCode,
    payload,
    platform,
    referrerUrl,
  };

  useEffect(() => {
    if (router.isReady) {
      const queryJWT = router.query[jwtParam] as string;
      const queryLoungeCode = router.query[lcParam] as string;
      const queryReferrer = router.query[referrerParam] as string;
      const queryPlatform = router.query[platformParam] as string;
      const queryLanguage = router.query[ln] as string;
      const queryVersion = router.query[version] as string;

      const storageJWT = getItem(JWT);
      const storageLoungeCode = getItem(LOUNGE_CODE);

      const hasStoredData = storageJWT && storageLoungeCode;
      const hasQueryParams = queryJWT && queryLoungeCode;

      let jwt = '';
      let loungeCode = '';
      let language = 'en';
      let versionPDF: string = process.env.NEXT_PUBLIC_VERSION ?? '';
      let referrer = '';
      let platform = 'web';

      if (hasQueryParams) {
        sendInfoLog(BookingInfo.PARAM_FOUND, logData);
        jwt = queryJWT;
        loungeCode = queryLoungeCode;
        referrer = queryReferrer || '';
        platform = queryPlatform || 'web';
        language = formatLanguageParam(queryLanguage) || 'en';
        versionPDF = queryVersion || process.env.NEXT_PUBLIC_VERSION || '';
      } else if (hasStoredData) {
        jwt = getItem(JWT) || queryJWT;
        loungeCode = getItem(LOUNGE_CODE) || queryLoungeCode;
        referrer = getItem(REFERRER) || '';
        platform = getItem(PLATFORM) || 'web';
        language = getItem(LANGUAGE) || 'en';
        versionPDF = getItem(VERSION) || process.env.NEXT_PUBLIC_VERSION || '';
        sendInfoLog(BookingInfo.PARAM_FOUND, logData);
      }

      setPlatform(queryPlatform);

      if (!loungeCode || !jwt) {
        sendErrorLog(BookingError.UNABLE_TO_RETRIEVE_PARAMS, logData);

        setPayloadError(translations.generic.error.tokenNoFound);
        return;
      }

      setItem(LOUNGE_CODE, loungeCode);
      setItem(LANGUAGE, language);
      setItem(JWT, jwt);
      setItem(REFERRER, referrer);
      setItem(PLATFORM, platform);
      setLoungeCode(loungeCode);
      setJWT(jwt);
      setReferrerUrl(referrer);
      setPlatform(platform);
      setItem(VERSION, versionPDF);
      setLanguage(language);

      let payload: BridgePayload;

      const handleJWTVerification = async () => {
        try {
          payload = decodeJWT(jwt) as BridgePayload;
        } catch (e) {
          sendErrorLog(BookingError.JWT_DECODE_ERROR, {
            ...logData,
            error: e,
          });
          setPayloadError(translations.generic.error.tokenNoFound);
          return;
        }

        if (!isPayloadValid(payload)) {
          sendErrorLog(BookingError.JWT_VALIDATION_ERROR, {
            ...logData,
            payload,
          });
          setPayloadError(translations.generic.error.tokenNoFound);
          return;
        }

        setPayload(payload);

        verifyJWT(jwt, secret)
          .then((isValidJWT) => {
            if (!isValidJWT) {
              sendErrorLog(BookingError.JWT_VALIDATION_ERROR, logData);
              setPayloadError(translations.lounge.errors.server.title);
            }
          })
          .catch((e) => {
            sendErrorLog(BookingError.JWT_VALIDATION_ERROR, {
              ...logData,
              error: e,
            });

            setPayloadError(translations.lounge.errors.server.title);
          });
      };

      handleJWTVerification();
    }
  }, [router]);

  const findLinkedAccount = useCallback(
    (linkedAccounts: LinkedAccount[] = []) =>
      linkedAccounts.find(accountIsEqual(payload)),
    [payload]
  );

  // user already logged-in
  useEffect(() => {
    if (
      payload &&
      !session.loading &&
      !matchedAccount?.id &&
      !router.pathname.includes('/auth')
    ) {
      const { userId } = session;

      if (userId) {
        sendInfoLog(BookingInfo.CONSUMER_ID, {
          ...logData,
          userId,
        });

        fetchConsumer({
          variables: {
            getConsumerById: userId,
          },
        }).then(({ data }) => {
          sendInfoLog(BookingInfo.CONSUMER_RESPONSE, {
            ...logData,
            reponse: JSON.stringify(data || null),
          });

          const consumer = data?.getConsumerByID;

          if (language !== consumer?.locale) {
            updateConsumerCall({
              variables: {
                consumerInput: {
                  emailAddress: consumer?.emailAddress,
                  locale: language,
                },
              },
            });
          }

          if (consumerIsValid(consumer)) {
            setConsumerData(data);

            const consumerLinkedAccount = findLinkedAccount(
              data.getConsumerByID.linkedAccounts
            );

            if (consumerLinkedAccount) setMatchedAccount(consumerLinkedAccount);
            else signOut();
          } else {
            sendWarnLog(BookingWarn.CONSUMER_IS_NOT_VALID, logData);

            signOut();
          }
        });
      }
    }
  }, [payload, matchedAccount, router, session]);

  useEffect(() => {
    optimizely
      .onReady()
      .then(() => {
        setOptimizelyIsReady(true);
      })
      .catch((error) => {
        console.error('Optimizely failed to initialize:', error);
      });
  }, [optimizely]);

  useEffect(() => {
    if (!payload) return;

    const cachedJWT = decodeJWT(getItem(JWT) ?? '') as BridgePayload;
    if (!cachedJWT) return;

    if (
      payload.email === cachedJWT.email &&
      payload.externalId === cachedJWT.externalId &&
      payload.membershipNumber === cachedJWT.membershipNumber &&
      payload.sourceCode === cachedJWT.sourceCode
    )
      return;

    signOut();
  }, [jwt, payload]);

  return (
    <>
      <Head>
        <link
          href={`${
            payload?.accountProvider === LK
              ? '/lk-favicon.ico'
              : '/pp-favicon.ico'
          }`}
          rel="icon"
          sizes="any"
        />
      </Head>
      <PayloadContext.Provider
        value={{
          consumerData,
          consumerID: consumerData?.getConsumerByID.id,
          fetchConsumerError,
          fetchConsumerLoading,
          jwt,
          layoutError,
          layoutErrorParams,
          linkedAccountId: matchedAccount?.id,
          locale: language,
          loungeCode,
          membership: matchedAccount?.memberships?.[0],
          membershipType: payload?.membershipType,
          optimizelyIsReady,
          payload,
          payloadError,
          platform,
          referrerUrl,
          session,
          setConsumerData,
          setLayoutError,
          setLayoutErrorParams,
          setMatchedAccount,
          setPayload,
          setPayloadError,
          sourceCode: matchedAccount?.sourceCode ?? '',
          tracking: optimizely,
          userIsSignedIn: Boolean(jwt && !session.loading && session.userId),
        }}
      >
        {props.children}
      </PayloadContext.Provider>
    </>
  );
};
